import React from 'react';
import {
  Button, SimpleForm, DateInput, AutocompleteArrayInput, SelectArrayInput, SelectInput, ReferenceArrayInput, Toolbar,
  TextField, Datagrid, Edit, Create, List, DateField, Filter, TextInput, SaveButton, required, FormDataConsumer, BooleanInput
} from 'react-admin';
import RunReportIcon from '@material-ui/icons/PlayCircleFilledRounded';
import CloudDownload from '@material-ui/icons/CloudDownload';
import DataTable from 'react-data-table-component';
import { fetchUtils, usePermissions } from 'react-admin';
import { ExpandMore } from '@material-ui/icons';
import {Accordion, AccordionSummary, AccordionDetails, Box, Card} from "@material-ui/core";
import FilterCardTitle from '../common/filters/FilterCardTitle.js';
import { AlertComponent } from '../components/alerts.js';
import { DisclaimerComponent } from '../components/disclaimer.js';
import { LegendComponent } from '../components/legend.js';
import DeleteWithConfirmation from '../components/DeleteWithConfirmation.js';
import { EmptyList } from '../components/empty.js';

const choicesAdType = require('../components/adtypes.json');
const choicesSizes = require('../components/sizes.json');
const choicesDevices = require('../components/devices.json');
const countryChoices = require('../components/countries.json');
const browserChoices = require('../components/browsers.json');
const utils = require('../components/utils.js');
const dataSupport = require('../components/data_support.js');
const fetch = fetchUtils.fetchJson;


const BASE_URL = window.nobid_endpoint+'/carbon/discrepancies_report'
const requestOptions = {headers: new Headers()}
requestOptions.headers.set('accept', 'application/json')
const token = localStorage.getItem('token');
requestOptions.headers.set('Authorization', `Bearer ${token}`);

// https://github.com/jbetancur/react-data-table-component/blob/master/stories/DataTable/Basic/ExportCSV.stories.js
function convertArrayOfObjectsToCSV(array) {
  let result;

  const columnDelimiter = ',';
  const lineDelimiter = '\n';
  let keys = Object.keys(array[0]);
  keys = keys.filter(key => (key!=='code' && key!=='adx_ad_requests' && key!=='id'));

  result = '';
  result += keys.join(columnDelimiter);
  result += lineDelimiter;

  array.forEach(item => {
    let ctr = 0;
    keys.forEach(key => {
      if (key==='code' || key==='adx_ad_requests' || key==='id') return;
      if (ctr > 0) result += columnDelimiter;

      result += item[key];
      // eslint-disable-next-line no-plusplus
      ctr++;
    });
    result += lineDelimiter;
  });

  return result;
}

function downloadCSV(array) {
  array.forEach((row, i) => {
    delete array[i].id
    delete array[i].code
  })
  dataSupport.exportData(array)
}

const selectableBidders = [
  {"id": "Index Exchange (API)", "name": "Index Exchange (API)"},
  {"id": "Kargo (Email)", "name": "Kargo (Email)"},
  {"id": "OMS (Email)", "name": "OMS (Email)"}
];

// See components/utils.js REPORT_TIMEZONES.
const choicesInterval = [
  { id: 'day', name: 'Daily' },
  { id: 'week', name: 'Weekly' },
  { id: 'month', name: 'Monthly' },
  { id: 'year', name: 'Yearly' }
];

const choicesShowAdmin = [
  { id: '', name: '--- DIMENSIONS ---', disabled: true },
  { id: 'date', name: 'Date' },
  { id: 'publisher', name: 'Publisher' },
  { id: 'site', name: 'Site' },
  { id: 'bidder_name', name: 'Bidder', roles: ['admin', 'publisher'] },
  { id: 'prebid_bidder_name', name: 'Prebid Bidder', roles: ['admin', 'publisher'] },
  { id: 'country', name: 'Country' },
  { id: 'device', name: 'Device' },
  { id: 'browser', name: 'Browser' },
  { id: 'adsize', name: 'Ad Size' },
  { id: 'ad_type', name: 'Ad Type' }
]
const choicesShow = (permissions) => {
  if (!permissions) return [];
  return choicesShowAdmin.filter(x => { if (!x.roles || x.roles.includes(permissions.role)) return x });
}
function nameBySelector (selector) {
  return (choicesShowAdmin.find(x => x.id === selector)).name;
}
const BASE_COLUMNS = [
  { name: 'Date', selector: 'date', sortable: true, cell: row => row.date, definition: 'Date dimension.' },
  { name: 'Publisher', selector: 'publisher_name', sortable: true, definition: 'Publisher dimension.' },
  { name: 'Site', selector: 'site_name', sortable: true, definition: 'Site Name dimension.' },
  { name: 'Site ID', selector: 'site_id', sortable: true, definition: 'Site ID dimension.' },
  { name: 'Bidder', selector: 'bidder_name', sortable: true, definition: 'Bidder name.' },
  { name: 'Prebid Bidder', selector: 'prebid_bidder_name', sortable: true, definition: 'Prebid Bidder name.' },
  { name: 'Country', selector: 'country', sortable: true, definition: 'Country dimension.' },
  { name: 'Device', selector: 'device', sortable: true, definition: 'Device dimension.' },
  { name: 'Browser', selector: 'browser', sortable: true, definition: 'Browser dimension.' },
  { name: 'Ad Type', selector: 'ad_type', sortable: true, definition: 'Ad Type dimension. Banner, Video or Native.' },
  { name: 'Ad Size', selector: 'adsize', sortable: true, definition: 'Ad Size dimension.' },
  { name: '#Impressions', selector: 'impressions', sortable: true, cell: row => <span>{ utils.formatLong(row.impressions) }</span>, definition: 'Impressions served.' },
  { name: '$Revenue', selector: 'revenue', sortable: true, cell: row => <span>{ utils.formatDouble(row.revenue) }</span>, definition: 'Revenue generated by the impression in the currency defined in the Prebid wrapper.' },
  { name: '#Prebid Impressions', selector: 'prebid_impressions', sortable: true, cell: row => <span>{ utils.formatLong(row.prebid_impressions) }</span>, definition: 'Impressions reported by the Prebid Analytics Adapter.' },
  { name: '$Prebid Revenue', selector: 'prebid_revenue', sortable: true, cell: row => <span>{ utils.formatDouble(row.prebid_revenue) }</span>, definition: 'Revenue reported by the Prebid Analytics Adapter.' },
  { name: 'Impressions Spread', selector: 'impressions_spread', sortable: true, cell: row => utils.formatLong(row.impressions_spread) },
	{ name: '$ Revenue Spread', selector: 'revenue_spread', sortable: true, cell: row => utils.formatDouble(row.revenue_spread) },
	{ name: '% Impressions Disrepancy', selector: 'impressions_discrepancy', sortable: true, cell: row => utils.formatDouble(row.impressions_discrepancy, ' ') },
	{ name: '% Revenue Disrepancy', selector: 'revenue_discrepancy', sortable: true, cell: row => utils.formatDouble(row.revenue_discrepancy, ' ') },
];

const defaultColumnSelectors = ['impressions', 'revenue', 'prebid_impressions', 'prebid_revenue', 'impressions_spread',
  'revenue_spread', 'impressions_discrepancy', 'revenue_discrepancy'];

const dateRangeChoices = [
  { id: '0', name: 'Today' },
	{ id: 1, name: 'Yesterday' },
	{ id: 2, name: 'Last 2 Days' },
	{ id: 3, name: 'Last 3 Days' },
  { id: 5, name: 'Last 5 Days' },
	{ id: 7, name: 'Last 7 Days' },
	{ id: 8, name: 'Last 8 Days' },
	{ id: 10, name: 'Last 10 Days' },
  { id: 15, name: 'Last 15 Days' },
	{ id: 30, name: 'Last 30 Days' },
  { id: 'lm', name: 'Last Month' },
  { id: 'mtd', name: 'Month to Date' },
  { id: 'mty', name: 'Month to Yesterday' },
  { id: 'ytd', name: 'Year to Date' },
  { id: 'yty', name: 'Year to Yesterday' },
	{ id: 'custom', name: 'Custom' }
];

let columns = [];

class CustomToolbar extends React.Component {
  state = {
    data: [],
    loading: false,
    total: 0,
    perPage: 25,
    params : ['_end=1000000', '_order=ASC', '_sort=id', '_start=0'],
    baseUrl: BASE_URL
  };

  constructor(props) {
    super(props);
    this.parent = props.parent;
  }

  fetchData = () => {
    function updateState(that, headers, json) {
      if (json) for (let i in json) {
        let r = json[i];
        if (r.date) r.date = r.date.indexOf('T') > 0 ? r.date.substring(0, r.date.indexOf('T')) : r.date;
      }

      const showList = formData.show || [];
      const columnSelectors = defaultColumnSelectors.concat(showList.flatMap(x=>[x, `${x}_id`, `${x}_name`, `${x}_group`]));

      that.parent.setState({
        data: json,
        saved_data:json,
        columns: BASE_COLUMNS.filter(c => columnSelectors.includes(c.selector)),
        loading: false});
    }

    formData.show = formData.show || [];
    let show='';
    if (formData.show) formData.show.forEach(item => { show+='&show='+item; });
    defaultColumnSelectors.forEach(item => { show+='&show='+item; });

    this.parent.setState({ loading: true });
    let url = `${this.state.baseUrl}?${show}` +
      `${utils.formDataFieldsQueryStringFormat(
          formData,
          ['interval', 'date_range', 'start_date', 'end_date', 'publisher_id', 'site_id', 'bidder_name', 'prebid_bidder_name',
          'ad_type', 'device', 'adsize', 'country', 'timezone', 'browser']
      )}`;
    const requestOptions = {headers: new Headers()}
    requestOptions.headers.set('accept', 'application/json')
    const token = localStorage.getItem('token');
    requestOptions.headers.set('Authorization', `Bearer ${token}`);
    fetch(url, requestOptions).then(({ headers, json }) => {
    	updateState(this, headers, json)
    });
  }
  handleClick = () => {
	  this.fetchData();
  }

  render () {
    return (
      <Toolbar>
        <SaveButton style={{marginRight: '25px'}} variant='contained' 
          color='primary' disabled={this.parent.state.loading} {...this.props} />
        <Button label="Run Report" onClick={this.handleClick} props={this.props} variant="contained"
          style={{marginRight: '25px', padding: '7px', width: '160px'}} color="primary" startIcon={<RunReportIcon />} disabled={this.parent.state.loading}/>
      </Toolbar>
    )
  }
}

const PubReportTitle = ({ record }) => {
  return <span>Analytics Report {record ? `"${record.name}"` : ''}</span>;
};

const PubReportFilters = (props) => (
  <Filter {...props}>
    <TextInput label="Search" source="search_name" alwaysOn resettable />
  </Filter>
);
export const CarbonDiscrepanciesList = ({ permissions, ...props }) => (
  <List {...props} empty={<EmptyList name="Discrepancies Report" />} title="Discrepancies Reports" perPage={25} bulkActionButtons={false} filters={<PubReportFilters />} 
  exporter={false} >
    <Datagrid rowClick="edit">
      <TextField source="name" label="Name" />
      <DateField source="updated_at" label="Update Date" />
      <DateField source="created_at" label="Creation Date" />
      <DeleteWithConfirmation {...props}/>
    </Datagrid>
  </List>
);

let formData = {};
function setFormData(formdata) {
  formData = formdata;
}

const hasActiveFilters = (record) => {
  const filters = ['publisher_id', 'site_id', 'bidder_name', 'country', 'adsize', 'ad_type', 'device', 'browser', 'tldomain'];
  return record && filters.some( f => record[f] && record[f].length > 0)
}

class PubReportComponent extends React.Component {
  state = {
    data: [],
    loading: false,
    total: 0,
    perPage: 25,
    params : ['_end=1000000', '_order=ASC', '_sort=id', '_start=0'],
    baseUrl: BASE_URL,
    dateRange: this.props.record.date_range
  };
  
  render() {
    const { loading, data, dateRange } = this.state;
    const { permissions, formType, record } = this.props;

    return (
      <div>
        <AlertComponent />
        <Card>
          <SimpleForm toolbar={<CustomToolbar parent={this} />} style={{ width: '100%', backgroundColor: 'inherit', borderRadius: '5px' }} {...this.props}>
            <TextInput source="name" label="Report Name" style={{ width: '400px' }} validate={required()} />
            <div style={{ width: '100%', overflow: 'hidden', display: 'flex', columnGap: '1em' }} id="wrapper2">
              <FormDataConsumer>
                {({ formData }) => setFormData(formData) }
              </FormDataConsumer>
              <SelectInput label="Interval" source="interval" choices={choicesInterval} defaultValue='day' 
                disabled={loading}/>
              <div>
                <SelectInput label="Date Range" source="date_range"
                  onChange={value => this.setState({ dateRange: value.target.value })}
                  choices={dateRangeChoices}
                  defaultValue="mtd"
                  disabled={loading} />
                <div style={{ float: 'right', paddingLeft: '5px', display: ('custom' !== dateRange) ? 'none' : 'block' }}>
                  <div style={{ float: 'left', paddingRight: '5px', display: ('custom' !== dateRange) ? 'none' : 'block' }}>
                    <DateInput label="Start Date" source="start_date" allowEmpty disabled={loading}
                      style={{ display: ('custom' !== dateRange) ? 'none' : 'block' }} />
                  </div>
                  <div style={{ float: 'left', display: ('custom' !== dateRange) ? 'none' : 'block' }}>
                    <DateInput label="End Date" source="end_date" allowEmpty disabled={loading}
                      style={{ display: ('custom' !== dateRange) ? 'none' : 'block' }} />
                  </div>
                </div>
              </div>
              <div>
              <utils.ReportTimezoneSelectInput disabled={loading}/>
              </div>
            </div>

            <Accordion defaultExpanded={true} style={{ marginBottom: '20px'}} fullWidth>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <FilterCardTitle title="Dimensions / Metrics" values={true} />
              </AccordionSummary>
              <AccordionDetails>
              <div style={{alignSelf: 'flex-start', width: '100%'}}>
                <SelectArrayInput label="Show"
                  size="small"
                  source="show" 
                  choices={choicesShow(permissions)}
                  defaultValue={['date']}
                  style={{alignSelf: 'flex-start', width: '100%'}}
                  disabled={loading} />
              </div>
              </AccordionDetails>
            </Accordion>
              
            <Accordion defaultExpanded={formType==="create"} style={{ marginBottom: '20px'}} fullWidth>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <FilterCardTitle title="Filters" values={hasActiveFilters(record)} />
              </AccordionSummary>
              <AccordionDetails>
                <Box style={{ width: '100%' }}>
                  <div style={{ width: '100%', overflow: 'hidden', display: 'flex', columnGap: '1em' }} id="wrapper3">
                    <div style={{flex: 1}}>
                      <ReferenceArrayInput 
                        source="publisher_id" 
                        label="Publisher" 
                        reference="publishers"
                        basePath="publishers"
                        perPage={15} 
                        sort={{ field: 'name', order: 'ASC' }}
                        filterToQuery={searchText => ({ search_name: searchText })}
                        allowEmpty
                        disabled={loading}>
                        <AutocompleteArrayInput disabled={loading}
                          label="Publishers"
                          optionText="name"
                          optionValue="code"
                          fullWidth/>
                      </ReferenceArrayInput>
                    </div>

                    {permissions &&
                      <div style={{flex: 1}}>
                        <ReferenceArrayInput 
                          source="site_id" 
                          label="Site" 
                          reference="sites"
                          basePath='sites'
                          perPage={15} 
                          sort={{ field: 'name', order: 'ASC' }}
                          filterToQuery={searchText => utils.siteFormFilter(searchText, formData)}
                          allowEmpty
                          disabled={loading}>
                          <AutocompleteArrayInput disabled={loading} label="Sites" 
                            optionText={ x => utils.formatSiteName(x) }
                            optionValue="code"
                            fullWidth/>
                        </ReferenceArrayInput>
                      </div>
                    }
                  
                    <div style={{flex: 1}}>
                      <AutocompleteArrayInput label="Bidders" 
                      source="bidder_name" 
                      choices={selectableBidders}
                      disabled={loading}
                      fullWidth />
                    </div>
                  </div>
              
                  <div style={{ width: '100%', overflow: 'hidden', display: 'flex', columnGap: '1em' }} >
                    <div style={{flex: 1}}>
                      <SelectArrayInput label="Devices" 
                        source="device" 
                        choices={choicesDevices} 
                        disabled={loading}
                        fullWidth />
                    </div>

                    <div style={{flex: 1}}>
                      <SelectArrayInput label="Ad Sizes" 
                        source="adsize" 
                        choices={choicesSizes} 
                        disabled={loading}
                        fullWidth />
                    </div>
                    
                    <div style={{flex: 1}}>
                      <SelectArrayInput label="Ad Type" 
                        source="ad_type" 
                        choices={choicesAdType}
                        disabled={loading}
                        fullWidth />
                    </div>
                  </div>

                  <div style={{ width: '100%', overflow: 'hidden', display: 'flex', columnGap: '1em' }} >
                    <div style={{flex: 1}}>
                      <SelectArrayInput label="Countries"
                        source="country"
                        choices={countryChoices}
                        disabled={loading}
                        fullWidth/>
                    </div>
                    <div style={{flex: 1}}>
                      <SelectArrayInput label="Browsers"
                        source="browser"
                        choices={browserChoices}
                        disabled={loading}
                        fullWidth/>
                    </div>
                  </div>
                </Box>
              </AccordionDetails>
            </Accordion>
          </SimpleForm>
        </Card>
        <div style={{ height: '15px', backgroundColor: 'transparent'}} />
        <Card>
          <DataTable
            title="Analytics Report"
            theme={window.nobid_table_theme()}
            columns={this.state.columns}
            data={data}
            progressPending={loading}
            pagination
            fixedHeader
            paginationPerPage={30}
            paginationRowsPerPageOptions={[10, 20, 30, 50, 100]}
            dense
            highlightOnHover
            subHeader={!loading}
            subHeaderComponent={
              (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <input type="text" id='search_box' label="Search" 
                  variant="outlined" 
                  style={{ margin: '5px' }} 
                  onChange={(target) => dataSupport.filterData(this)}/>
                  <CloudDownload 
                  label="Export"
                  style={{ margin: '5px' }} 
                  color="action" 
                  onClick={() => downloadCSV(this.state.data)}
                  />
                </div>
              )
            }
          />
          
          <DisclaimerComponent />

          <LegendComponent columns={BASE_COLUMNS} choices={choicesShow(permissions)}/>

        </Card>
      </div>
    )
  }
};

export const CarbonDiscrepanciesEdit = (props) => (
  <Edit title={<PubReportTitle />}  mutationMode="pessimistic" {...props}>
    <PubReportComponent formType="edit" permissions={props.permissions} {...props} />
  </Edit>
);

export const CarbonDiscrepanciesCreate = (props) => (
  <Create title="Create Analytics Report" mutationMode="pessimistic" {...props}>
    <PubReportComponent formType="create" permissions={props.permissions} {...props} />
  </Create>
);
